import { baseApi } from "./baseApi";

export type ServiceType = "CATERING" | "PRIVATE_CHEF";

export type ServiceRequestStatus =
  | "PENDING"
  | "CONTACTED"
  | "IN_PROGRESS"
  | "CONFIRMED"
  | "COMPLETED"
  | "CANCELLED";

export interface ServiceRequest {
  id: string;
  type: ServiceType;
  status: ServiceRequestStatus;
  fullName: string;
  email: string;
  phoneNumber: string;
  preferredContactMethod: string;
  eventDate: string | null;
  eventTime: string | null;
  eventLocation: string | null;
  serviceDates: string | null;
  serviceTime: string | null;
  location: string | null;
  numberOfGuests: number;
  serviceDetails: Record<string, unknown>;
  budgetRange: string;
  specialNotes: string | null;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
  respondedAt: string | null;
}

export interface CateringRequestData {
  fullName: string;
  email: string;
  phoneNumber: string;
  preferredContactMethod: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  numberOfGuests: number;
  serviceType: string;
  menuPreference: string;
  specificDishes?: string;
  servingStyle: string;
  budgetRange: string;
  specialNotes?: string;
  referralSource: string;
}

export interface PrivateChefRequestData {
  fullName: string;
  email: string;
  phoneNumber: string;
  preferredContactMethod: string;
  serviceType: string;
  serviceDates: string;
  serviceTime: string;
  location: string;
  numberOfGuests: number;
  cuisinePreference: string;
  specificDishes?: string;
  dietaryRestrictions?: string;
  spiceLevel: string;
  numberOfCourses: string;
  servingStyle: string;
  ingredientsProvided: string;
  budgetRange: string;
  additionalNotes?: string;
}

export interface UpdateStatusData {
  status: ServiceRequestStatus;
  adminNotes?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ServiceRequestFilters {
  type?: ServiceType;
  status?: ServiceRequestStatus;
  page?: number;
  limit?: number;
}

export interface PaginatedServiceRequestsResponse {
  success: boolean;
  data: ServiceRequest[];
  pagination: PaginationMeta;
}

/**
 * Service Request API slice
 */
export const serviceRequestApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create catering request (public)
    createCateringRequest: builder.mutation<
      { success: boolean; data: ServiceRequest; message?: string },
      CateringRequestData
    >({
      query: (data) => ({
        url: "/service-requests/catering",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ServiceRequest"],
    }),

    // Create private chef request (public)
    createPrivateChefRequest: builder.mutation<
      { success: boolean; data: ServiceRequest; message?: string },
      PrivateChefRequestData
    >({
      query: (data) => ({
        url: "/service-requests/private-chef",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ServiceRequest"],
    }),

    // Get all service requests (admin only) with pagination
    getAllServiceRequests: builder.query<PaginatedServiceRequestsResponse, ServiceRequestFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.type) searchParams.append("type", params.type);
        if (params?.status) searchParams.append("status", params.status);
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return {
          url: `/service-requests${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["ServiceRequest"],
    }),

    // Get service request by ID (admin only)
    getServiceRequestById: builder.query<
      { success: boolean; data: ServiceRequest },
      string
    >({
      query: (id) => ({
        url: `/service-requests/${id}`,
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "ServiceRequest", id }],
    }),

    // Update service request status (admin only)
    updateServiceRequestStatus: builder.mutation<
      { success: boolean; data: ServiceRequest; message?: string },
      { id: string; data: UpdateStatusData }
    >({
      query: ({ id, data }) => ({
        url: `/service-requests/${id}/status`,
        method: "PATCH",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ServiceRequest", id },
        "ServiceRequest",
      ],
    }),

    // Delete service request (admin only)
    deleteServiceRequest: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/service-requests/${id}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["ServiceRequest"],
    }),
  }),
});

export const {
  useCreateCateringRequestMutation,
  useCreatePrivateChefRequestMutation,
  useGetAllServiceRequestsQuery,
  useGetServiceRequestByIdQuery,
  useUpdateServiceRequestStatusMutation,
  useDeleteServiceRequestMutation,
} = serviceRequestApi;
